import { useEffect, useRef, useMemo, useState } from 'react';
import type { Player } from '@flip7/shared';
import { Card } from './Card';
import { Avatar } from './Avatar';
import { ConfirmModal } from './ConfirmModal';

interface PlayerStripProps {
  players: Player[];
  currentPlayerIndex: number;
  myPlayerId?: string | null;
  selectedPlayerId?: string | null;
  onPlayerClick?: (playerId: string) => void;
  onKickPlayer?: (playerId: string) => void;
}

function statusBadge(status: string | undefined, cardCount: number) {
  switch (status) {
    case 'stayed': return { text: 'SETTLED', color: 'var(--color-stay)', bg: 'var(--color-stay-bg)' };
    case 'busted': return { text: 'BUST', color: 'var(--color-bust)', bg: 'var(--color-bust-bg)' };
    case 'frozen': return { text: '\u2744 FROZEN', color: 'var(--color-freeze)', bg: 'var(--color-freeze-bg)' };
    case 'active': return { text: `${cardCount} card${cardCount !== 1 ? 's' : ''}`, color: 'var(--text-muted)', bg: 'transparent' };
    default: return null;
  }
}

export function PlayerStrip({ players, currentPlayerIndex, myPlayerId, selectedPlayerId, onPlayerClick, onKickPlayer }: PlayerStripProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const [kickTarget, setKickTarget] = useState<{ id: string; name: string } | null>(null);

  // Sort players: current user always first, rest in original order
  const sortedPlayers = useMemo(() => myPlayerId
    ? [...players].sort((a, b) => {
        if (a.id === myPlayerId) return -1;
        if (b.id === myPlayerId) return 1;
        return 0;
      })
    : players, [players, myPlayerId]);

  // Auto-scroll to current player on turn change
  useEffect(() => {
    const currentPlayer = players[currentPlayerIndex];
    if (!currentPlayer) return;
    const sortedIndex = sortedPlayers.findIndex(p => p.id === currentPlayer.id);
    const el = itemRefs.current.get(sortedIndex);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [currentPlayerIndex, players, sortedPlayers]);

  return (
    <>
    <div ref={scrollRef} className="player-strip">
      {sortedPlayers.map((player, index) => {
        const originalIndex = players.findIndex(p => p.id === player.id);
        const isCurrent = originalIndex === currentPlayerIndex;
        const isMe = player.id === myPlayerId;
        const isSelected = selectedPlayerId != null && player.id === selectedPlayerId;
        const cardCount = player.currentRound?.cards.length ?? 0;
        const badge = statusBadge(player.currentRound?.status, cardCount);
        const isDisconnected = !player.isConnected;
        const isFrozen = player.currentRound?.status === 'frozen';

        const chipClass = [
          'player-chip',
          isFrozen ? 'player-chip--frozen' : '',
          isSelected || isCurrent ? 'player-chip--current' : '',
          isMe && !isCurrent && !isSelected ? 'player-chip--me' : '',
          isDisconnected ? 'player-chip--disconnected' : '',
        ].filter(Boolean).join(' ');

        return (
          <button
            key={player.id}
            ref={(el) => { if (el) itemRefs.current.set(index, el); }}
            onClick={() => onPlayerClick?.(player.id)}
            className={chipClass}
            style={{ cursor: onPlayerClick ? 'pointer' : 'default' }}
            aria-label={`${player.name}${isMe ? ' (you)' : ''}: ${player.totalScore} points${isCurrent ? ' (current)' : ''}${isSelected ? ' (selected)' : ''}`}
          >
            <Avatar config={player.avatarConfig} emoji={player.avatar} size={24} />
            <span className="player-chip__name" style={{
              color: isCurrent || isSelected ? 'var(--gold-dim)' : isMe ? 'var(--maroon)' : undefined,
              textDecoration: isDisconnected ? 'line-through' : undefined,
            }}>
              {player.isBot && '\uD83E\uDD16 '}
              {player.name}
              {isMe && <span style={{ fontSize: '0.45rem', opacity: 0.5, marginLeft: '2px' }}>(you)</span>}
            </span>
            {player.title && (
              <span style={{ fontSize: '0.5rem', fontWeight: 600, color: 'var(--gold-dim)', fontStyle: 'italic' }}>
                {player.title}
              </span>
            )}
            <span className="player-chip__score">{player.totalScore}</span>
            {player.currentRound && (
              <span className="player-chip__round-score">+{player.currentRound.roundScore}</span>
            )}
            {badge && (
              <span className="player-chip__badge" style={{ color: badge.color, background: badge.bg }}>
                {badge.text}
                {player.currentRound?.status === 'busted' && player.currentRound.bustCard?.type === 'number'
                  ? ` (drew ${player.currentRound.bustCard.value})`
                  : ''}
              </span>
            )}
            {player.currentRound && player.currentRound.cards.length > 0 && (
              <div className="player-chip__cards hand">
                {player.currentRound.cards.map((card, i) => (
                  <div key={`${card.type}-${card.value}-${i}`} className="card-deal">
                    <Card card={card} />
                  </div>
                ))}
              </div>
            )}
            {isDisconnected && (
              <span style={{ fontSize: '0.5rem', color: '#999', fontWeight: 600 }}>Offline</span>
            )}
            {isSelected && !player.isHost && onKickPlayer && (
              <button
                className="player-chip__kick"
                onClick={(e) => {
                  e.stopPropagation();
                  setKickTarget({ id: player.id, name: player.name });
                }}
              >
                Kick
              </button>
            )}
          </button>
        );
      })}
    </div>
    <ConfirmModal
      open={!!kickTarget}
      title="Kick Player"
      message={`Kick ${kickTarget?.name ?? ''}?`}
      confirmLabel="Kick"
      variant="danger"
      onConfirm={() => {
        if (kickTarget && onKickPlayer) {
          onKickPlayer(kickTarget.id);
        }
        setKickTarget(null);
      }}
      onCancel={() => setKickTarget(null)}
    />
    </>
  );
}
